import { createContext, useContext, useEffect } from "react";

import { DJINN_OPEN_HASH, replyFromHash } from "./link";

/** Where a link to the sheet came from: an email says so in a banner, a ledger row does not. */
export type DjinnArrival = "email" | "ledger";

/**
 * Where the sheet opens. A reply opens it at that card with a way back to the latest;
 * nothing opens it on the thread's end.
 */
export interface DjinnOpenTarget {
  replyId?: string;
  from?: DjinnArrival;
  /**
   * The control that asked, when there is one: the light leaves the spout of the lamp inside it,
   * or of the top bar's lamp when it holds none (a glint on a number).
   */
  origin?: Element | null;
  /** The home page is handing over words (`djinn/home/asks`): a dock that is already open stays where it is. */
  asking?: boolean;
}

export type OpenDjinn = (target?: DjinnOpenTarget) => void;

/** Provided by the frame, which owns the sheet; a page that is not in the frame gets a no-op. */
export const DjinnOpenContext = createContext<OpenDjinn>(() => {});

/** Opens the chat sheet from a page. */
export function useOpenDjinn(): OpenDjinn {
  return useContext(DjinnOpenContext);
}

/** The frame's opener, set while the frame is mounted, for a caller that is not under it (a toast, an email link). */
let opener: OpenDjinn | null = null;

/** The frame registers its opener here for the time it is on screen. */
export function useRegisterDjinnOpener(open: OpenDjinn): void {
  useEffect(() => {
    opener = open;
    return () => {
      if (opener === open) opener = null;
    };
  }, [open]);
}

/**
 * Opens the sheet at a reply, from anywhere: the ledger's rows, a toast. Returns false
 * when no frame is on screen to open it.
 */
export function openDjinnAt(replyId: string, from: DjinnArrival = "ledger"): boolean {
  if (!opener) return false;
  opener({ replyId, from });
  return true;
}

/**
 * A link to `#r-<reply id>` (the email's) opens the sheet at that card once the frame is
 * up, and again whenever the hash changes to another one.
 */
export function useOpenFromHash(open: OpenDjinn, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    const check = () => {
      const replyId = replyFromHash(window.location.hash);
      if (replyId) open({ replyId, from: "email" });
      else if (window.location.hash === `#${DJINN_OPEN_HASH}`) open({});
    };
    check();
    window.addEventListener("hashchange", check);
    return () => {
      window.removeEventListener("hashchange", check);
    };
  }, [open, enabled]);
}
